import React from 'react';
import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useStaticQuery, graphql } from 'gatsby';

const borderAnimation = keyframes`
  0% {
    background-position: 50% 100%;
  }
  100% {
    background-position: 50% 0%;
  }
`;

const visibleAnimation = keyframes`
  0% {
    background-position: 50% 0%;
  }
  100% {
    background-position: 50% 100%;
  }
`;

const SidebarContainer = styled(motion.div)`
  position: fixed;
  border: 2px solid black;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background: white;
  width: 60px;
  height: 99.7vh;
  cursor: pointer;
  z-index: ${({ $zIndex }) => $zIndex};

  ${({ $isVisible }) =>
    $isVisible
      ? css`
          background: black;
          color: white;
          &:hover {
            background: linear-gradient(to bottom, black 70%, white 90%);
            background-size: 100% 140%;
            animation: ${visibleAnimation} 1s ease-in-out forwards;
          }
        `
      : css`
          &:hover {
            background: linear-gradient(to bottom, white 30%, black 80%);
            background-size: 100% 120%;
            animation: ${borderAnimation} 1.5s ease-in-out forwards;
          }
        `}

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    right: 20px;
    bottom: ${({ $index }) => 20 + $index * 80}px;
    top: auto;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    ${({ $isVisible }) =>
      $isVisible &&
      css`
        background: black;
        color: white;
      `}

    &:hover {
      transform: scale(1.1);
      animation: none;
    }
  }
`;

const IconWrapper = styled.div`
  margin-top: 1rem;
  font-size: 1.5rem;
  color: ${({ $isVisible }) => ($isVisible ? 'white' : 'black')};

  @media (max-width: 768px) {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 1.8rem; // Slightly larger icons for mobile
  }
`;

const LogoWrapper = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  border-radius: 50%;
  background-color: #000;
  position: relative;
  left: 30px;

  .gatsby-image-wrapper {
    width: 30px !important; /* Force width */
    height: 30px !important; /* Force height */
    padding: 10px;
  }

  @media (max-width: 768px) {
    margin: auto;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    left: 0;
    padding: 0;

    .gatsby-image-wrapper {
      width: 30px !important;
      height: 30px !important;
      padding: 0;
    }
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: 1rem;
  font-size: 1.5rem;
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  text-align: center;
  color: ${({ $isVisible, $isHovered }) =>
    $isVisible
      ? $isHovered
        ? 'black'
        : 'white'
      : $isHovered
      ? 'white'
      : 'black'};

  @media (max-width: 768px) {
    display: none;
  }
`;


const Sidebar = ({
  id,
  icon,
  title,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onReturnClick,
  variants,
  custom,
  animate,
  zIndex,
  isPageOpen,
  isHovered,
}) => {
  const isWasSelected = custom.wasSelected === id;
  const showButton = isPageOpen && isWasSelected;

  const data = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "logo.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 30
            height: 30
            placeholder: BLURRED
            layout: FIXED
          )
        }
      }
    }
  `);

  const logoImage = getImage(data.logo);

  const renderContent = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    if (isMobile) {
      if (showButton) {
        return (
          <LogoWrapper $isVisible={showButton}>
            <GatsbyImage
              image={logoImage}
              alt="Logo"
              style={{ width: '30px', height: '30px' }}
            />
          </LogoWrapper>
        );
      }
      return <IconWrapper $isVisible={showButton}>{icon}</IconWrapper>;
    }

    return (
      <>
        <IconWrapper $isVisible={showButton}>{icon}</IconWrapper>
        {showButton && (
          <LogoWrapper $isVisible={showButton}>
            <GatsbyImage
              image={logoImage}
              alt="Logo"
              style={{ width: '30px', height: '30px' }}
            />
          </LogoWrapper>
        )}
      </>
    );
  };

  return (
    <SidebarContainer
      onClick={isWasSelected ? onReturnClick : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      variants={variants}
      custom={custom}
      animate={animate}
      $zIndex={zIndex}
      $isVisible={showButton}
      $index={custom.index}
    >
      {renderContent()}
      <TitleWrapper $isVisible={showButton} $isHovered={isHovered}>
        {title}
      </TitleWrapper>
    </SidebarContainer>
  );
};

export default Sidebar;
